export const SEEDANCE_STORAGE_KEYS = {
  apiKey: 'ss_key',
  systemToken: 'ss_system_token',
  customGroupId: 'ss_custom_group_id',
  configUserId: 'ss_user_id',
  workspace: 'ss_workspace',
  librarySelectedGroupId: 'ss_lib_selected_group_id',
  /** IndexedDB（SeedanceDB）：当前所选素材组素材清单缓存 `{ groupId, assets }` */
  libraryGroupAssetsSnapshot: 'ss_library_group_assets_snapshot',
  /** IndexedDB（SeedanceDB）：按素材 ID 跨工作台/素材库共享的预览 URL 索引 */
  libraryAssetPreviewCache: 'ss_library_asset_preview_cache',
  /** `light` | `dark` */
  theme: 'ss_theme',
  /** @deprecated 文生图记录已迁往 IndexedDB，仅用于一次性迁移读取 */
  imageGenGenerations: 'ss_image_gen_generations',
  /** 文生图页：用户已关闭「生成记录留存」顶栏提示 */
  imageGenRetentionNoticeDismissed: 'ss_image_gen_retention_notice_dismissed',
  /** IndexedDB：任务列表 task_id → 字幕擦除 task_id 映射 */
  eraseSubtitleTaskMap: 'ss_erase_subtitle_task_map',
  /** localStorage：任务列表 task_id → 用户自定义任务名 映射（JSON） */
  taskNameMap: 'ss_task_name_map',
  /** 真人人像库：认证成功后得到的素材组 ID */
  realPortraitGroupId: 'ss_real_portrait_group_id',
  /** 真人人像库：当前选中的绑定素材组 ID */
  realPortraitSelectedGroupId: 'ss_real_portrait_selected_group_id',
} as const;

/** 真人人像认证进行中的 byted_token（sessionStorage，关闭标签页即清除） */
export const REAL_PORTRAIT_PENDING_BYTED_TOKEN_KEY = 'ss_real_portrait_pending_byted_token';

export const IMAGE_GEN_IDB_KEY = 'ss_image_gen_generations_idb';

/** SSR/测试环境中没有 window，读取 localStorage 时统一走这里兜底。 */
export const getLocalStorageValue = (key: string) => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(key) || '';
};

/** localStorage 写入统一入口，后续如果要加加密/迁移可集中处理。 */
export const setLocalStorageValue = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, value);
};

/** localStorage 删除统一入口，避免业务文件直接关心存储细节。 */
export const removeLocalStorageValue = (key: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
};

export type SeedanceWorkspaceIndex = {
  version: 1;
  nodes: Array<{
    id: string;
    type: 'image' | 'video' | 'audio';
    name: string;
    sourceAssetId?: string;
    connected: boolean;
    fileSize?: number;
    fileType?: string;
    duration?: number;
  }>;
  prompts: Array<{ id: number; text: string }>;
  promptSeq: number;
  nodeSeq: number;
  params: {
    ratio: string;
    resolution: string;
    duration: number;
    model: string;
    subtitleEnabled: boolean;
  };
};

const isWorkspaceIndex = (value: any): value is SeedanceWorkspaceIndex =>
  Boolean(
    value &&
      value.version === 1 &&
      Array.isArray(value.nodes) &&
      Array.isArray(value.prompts) &&
      typeof value.nodeSeq === 'number' &&
      value.params &&
      typeof value.params === 'object',
  );

export const loadSeedanceWorkspaceIndex = (): SeedanceWorkspaceIndex | null => {
  const raw = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.workspace);
  if (!raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return isWorkspaceIndex(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveSeedanceWorkspaceIndex = (index: SeedanceWorkspaceIndex): boolean => {
  if (typeof window === 'undefined' || !isWorkspaceIndex(index)) return false;
  try {
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.workspace, JSON.stringify(index));
    return true;
  } catch {
    return false;
  }
};

/**
 * Seedance 工作台 IndexedDB 存储。
 *
 * 目前只用一个 store：保留旧工作台快照迁移和文生图 JSON 等数据；保持容错，不让存储异常拖垮页面。
 */
export const seedanceWorkspaceDb = {
  db: null as IDBDatabase | null,
  init(): Promise<void> {
    if (seedanceWorkspaceDb.db) return Promise.resolve();
    return new Promise((resolve) => {
      const req = indexedDB.open('SeedanceDB', 3);
      req.onupgradeneeded = (e) => {
        const d = (e.target as IDBOpenDBRequest).result;
        if (!d.objectStoreNames.contains('store')) d.createObjectStore('store');
      };
      req.onsuccess = (e) => {
        seedanceWorkspaceDb.db = (e.target as IDBOpenDBRequest).result;
        resolve();
      };
      req.onerror = () => resolve();
    });
  },
  get<T = any>(k: string): Promise<T | null> {
    return new Promise((resolve) => {
      if (!seedanceWorkspaceDb.db) return resolve(null);
      try {
        const tx = seedanceWorkspaceDb.db.transaction('store', 'readonly');
        const r = tx.objectStore('store').get(k);
        r.onsuccess = () => resolve((r.result as T) ?? null);
        r.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  },
  set(k: string, v: any) {
    if (!seedanceWorkspaceDb.db) return;
    try {
      const tx = seedanceWorkspaceDb.db.transaction('store', 'readwrite');
      tx.objectStore('store').put(v, k);
    } catch {}
  },
  setAsync(k: string, v: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!seedanceWorkspaceDb.db) {
        resolve();
        return;
      }
      try {
        const tx = seedanceWorkspaceDb.db.transaction('store', 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB 写入失败'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB 已中止'));
        tx.objectStore('store').put(v, k);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  },
};

/** 首次升级时把旧版 IndexedDB 工作台快照迁移到 localStorage；之后不再从 IndexedDB 恢复工作台。 */
export async function migrateSeedanceWorkspaceIndex(): Promise<SeedanceWorkspaceIndex | null> {
  const existing = loadSeedanceWorkspaceIndex();
  if (existing) return existing;

  let legacy: any;
  try {
    await seedanceWorkspaceDb.init();
    legacy = await seedanceWorkspaceDb.get<any>(SEEDANCE_STORAGE_KEYS.workspace);
  } catch {
    return null;
  }
  if (!legacy || !Array.isArray(legacy.nodes)) return null;

  const index: SeedanceWorkspaceIndex = {
    version: 1,
    nodes: legacy.nodes
      .filter((n: any) => n?.type === 'image' || n?.type === 'video' || n?.type === 'audio')
      .map((n: any) => ({
        id: String(n.id || ''),
        type: n.type,
        name: String(n.name || ''),
        ...(n.sourceAssetId ? { sourceAssetId: String(n.sourceAssetId) } : {}),
        connected: Boolean(n.connected),
        ...(typeof n.fileSize === 'number' ? { fileSize: n.fileSize } : {}),
        ...(n.fileType ? { fileType: String(n.fileType) } : {}),
        ...(typeof n.duration === 'number' ? { duration: n.duration } : {}),
      })),
    prompts: Array.isArray(legacy.prompts)
      ? legacy.prompts
          .filter((p: any) => p && typeof p.id === 'number')
          .map((p: any) => ({ id: p.id, text: String(p.text || '') }))
      : [{ id: 0, text: '' }],
    promptSeq:
      typeof legacy.promptSeq === 'number'
        ? legacy.promptSeq
        : Math.max(...(legacy.prompts || []).map((p: any) => Number(p?.id) + 1 || 1), 1),
    nodeSeq: typeof legacy.nodeSeq === 'number' ? legacy.nodeSeq : 1,
    params: {
      ratio: legacy.params?.ratio || '16:9',
      resolution: legacy.params?.resolution || '720p',
      duration: legacy.params?.duration || 10,
      model: legacy.params?.model || 'doubao-seedance-2-0-260128',
      subtitleEnabled: Boolean(legacy.params?.subtitleEnabled),
    },
  };
  saveSeedanceWorkspaceIndex(index);
  return index;
}

/** LocalStorage 常放不下 base64；IndexedDB 配额大得多。若存在旧数据则迁移后删除 localStorage 键。 */
export async function loadImageGenGenerationsJson(): Promise<string> {
  await seedanceWorkspaceDb.init();
  const fromIdb = await seedanceWorkspaceDb.get<string>(IMAGE_GEN_IDB_KEY);
  if (typeof fromIdb === 'string' && fromIdb.length > 0) {
    return fromIdb;
  }
  const legacy = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.imageGenGenerations);
  if (legacy.trim()) {
    try {
      await seedanceWorkspaceDb.setAsync(IMAGE_GEN_IDB_KEY, legacy);
      removeLocalStorageValue(SEEDANCE_STORAGE_KEYS.imageGenGenerations);
    } catch {
      /* 迁移写入失败时仍用 legacy 供当次展示 */
    }
    return legacy;
  }
  return '';
}

export async function saveImageGenGenerationsJson(json: string): Promise<void> {
  await seedanceWorkspaceDb.init();
  if (!seedanceWorkspaceDb.db) throw new Error('IndexedDB 不可用');
  await seedanceWorkspaceDb.setAsync(IMAGE_GEN_IDB_KEY, json);
}
